<template>
  <div class="comment-or-reply">
    <!-- Header -->
    <div class="comment-or-reply__header">
      <dd-button
        :class="{ 'comment-or-reply__header-button--active': isCommentActive }"
        size="small"
        type="text"
        class="comment-or-reply__header-button"
        @click="enableComment()"
      >Comment</dd-button>
      <dd-button
        v-if="!isForConversation && $permissions.isNotGuest && hasGuestUsers"
        :class="{ 'comment-or-reply__header-button--active': isInternalCommentActive }"
        size="small"
        type="text"
        class="comment-or-reply__header-button"
        @click="enableInternalComment()"
      >Internal Comment</dd-button>
      <dd-button
        v-if="isForConversation"
        :class="{ 'comment-or-reply__header-button--active': isReplyActive }"
        size="small"
        type="text"
        class="comment-or-reply__header-button"
        @click="enableReply()"
      >
        <span>{{replyToLabel}}</span>
        <i class="ddi ddi-return comment-or-reply__header-button-icon" />
      </dd-button>
    </div>
    <!-- End Header -->
    <div class="dd-divider" />
    <el-form ref="commentOrReply" :model="formData" autocomplete="off">
      <!-- Body -->
      <div :class="{ 'email-style': isReplyActive }" class="comment-or-reply__body">
        <div class="comment-or-reply__body-message-wrapper">
          <avatar
            v-if="$screen.isTabletOrDesktop($mq) && (isCommentActive || isInternalCommentActive)"
            :size="avatarSize"
            :person="author"
            class="comment-or-reply__body-message-avatar"
          />
          <!-- Reply to Customer -->
          <div v-if="isReplyActive && isEditing && isForConversation">
            <!-- To and CC -->
            <to-cc-inputs 
              :mailbox-id="entityId"
              :initial-to-customer="internalCustomer as IPersonWithEmail"
              :to-email="formData.to"
              :ccd-emails="formData.cc"
              @update-to-email="(val) => updateToField(val)"
              @update-ccd-emails="(val) => $emit('update-cc', val)"
            />
          </div>
          <el-form-item prop="message">
            <editor
              :key="placeholder"
              v-model="formData.message"
              :mention-users="mentionUsers"
              :autofocus="autoFocusEditor"
              :placeholder="placeholder"
              :class="editorClass"
              :signature="isReplyActive ? replySignature : ''"
              :saved-replies="isReplyActive ? savedReplies : []"
              :flat-bottom="expandEditor"
              allows-attachment
              allows-google-drive
              name="comment-reply"
              class="comment-or-reply__body-message-editor"
              @mentions-list="insertMentions"
              @files-list="addFileHandler"
              @file-list-removed="removeFileHandler"
              @click="openEditor"
            />
          </el-form-item>
        </div>
        <template v-if="expandEditor">
          <div v-if="($permissions.isNotGuest || ($permissions.isGuest && isAssignedToSelf)) && !isInternalCommentActive" class="comment-or-reply__body-options">
            <div :class="{'with-jobs' : hasJobs}" class="status-section">
              <!-- Current status -->
              <div class="current-status marker--comment-reply--current-status-col">
                <span class="action-title">Current Status</span>
                <status-selection-button
                  v-if="curStatus"
                  :status="curStatus"
                  :selected="formData.statusID===statusId"
                  :job-info="jobInfo"
                  set-assignee-to-current
                  @click="formData.statusID=statusId" />
              </div>
              <!-- Change status -->
              <div v-if="!isInternalCommentActive" class="change-status marker--comment-reply--change-status-col">
                <circle-arrow-icon :direction="moveStatusDirection" class="move-icon" />
                <span class="action-title">Update Status To</span>
                <div v-if="showStatusesSplayed" class="status-options-splayed">
                  <status-selection-button
                    v-for="status in splayedStatuses"
                    :key="status.id"
                    :status="status"
                    :selected="formData.statusID===status.id"
                    :job-info="jobInfo"
                    @click="formData.statusID=status.id;"
                  >{{ status.name }}
                  </status-selection-button>
                </div>
                <status-picker
                  v-else
                  v-model:selected-id="formData.statusID"
                  :statuses="statusOptions"
                  :job-info="jobInfo"
                  class="status-picker"
                />
              </div>
            </div>
            <!-- Assign and prioritize -->
            <div class="assign-and-prioritize marker--comment-reply--assign-and-prioritize-col">
              <span class="action-title">
                <template v-if="hasJobs">Prioritize</template>
                <template v-else>Assign and Prioritize</template>
              </span>
              <div class="picker-section">
                <!-- Assignee select -->
                <assignment-picker
                  v-if="!hasJobs"
                  v-model:selected-id="formData.assigneeID"
                  :options="assigneesOptions as IPersonWithEmail[]"
                  hide-name
                />
                <!-- Priority select -->
                <priority-picker
                  v-model:selected-id="formData.priorityID"
                  :is-disabled="$permissions.isGuest"
                  :options="priorityOptions"
                  hide-icon
                />
                <!-- Due date picker -->
                <due-date-picker
                  v-model="formData.dueDate"
                  :is-disabled="$permissions.isGuest"
                />
              </div>
            </div>
          </div>
          <div :class="{ 'email-style': isReplyActive }" class="comment-or-reply__body-submit">
            <!-- Reply button -->
            <template v-if="isReplyActive">
              <template v-if="hasRespondersSinceLastView">
                <el-tooltip
                  :visible="true"
                  placement="top"
                  popper-class="traffic-cop"
                >
                  <template #content>
                    <div
                      @click="$emit('confirmed-traffic-cop-note')"
                      v-html="trafficCopText"
                    />
                  </template>
                  <dd-button
                    color="dark-teal"
                    is-disabled
                    class="marker--comment-reply--reply-btn"
                    type="flat"
                    size="large"
                    @click="validateSubmitReply"
                  >
                    <i class="ddi ddi-email" />&nbsp;{{ submitButtonText }}
                  </dd-button>
                </el-tooltip>
              </template>
              <template v-else-if="possiblyMeantToComment">
                <el-tooltip
                  :visible="true"
                  placement="top"
                  popper-class="traffic-cop"
                >
                  <template #content>
                    <div
                      @click="enableComment"
                      v-html="possiblyMeantToCommentText"
                    />
                  </template>
                  <dd-button
                    :is-disabled="isInternalCustomerEmpty"
                    :is-loading="isSubmitting || isTrafficCopChecking"
                    color="dark-teal"
                    class="marker--comment-reply--reply-btn"
                    type="flat"
                    size="large"
                    @click="validateSubmitReply"
                  >
                    <i class="ddi ddi-email" />&nbsp;{{ submitButtonText }}
                  </dd-button>
                </el-tooltip>
              </template>
              <template v-else>
                <dd-button
                  :is-disabled="isInternalCustomerEmpty"
                  :is-loading="isSubmitting || isTrafficCopChecking"
                  color="dark-teal"
                  class="marker--comment-reply--reply-btn"
                  type="flat"
                  size="large"
                  @click="validateSubmitReply"
                >
                  <i class="ddi ddi-email" />&nbsp;{{ submitButtonText }}
                </dd-button>
              </template>
            </template>
            <!-- Comment button -->
            <template v-else>
              <template v-if="hasRespondersSinceLastView">
                <el-tooltip
                  :visible="true"
                  placement="top"
                  popper-class="traffic-cop"
                >
                  <template #content>
                    <div
                      @click="$emit('confirmed-traffic-cop-note')"
                      v-html="trafficCopText"
                    />
                  </template>
                  <dd-button
                    is-disabled
                    color="blue"
                    class="marker--comment-reply--comment-btn"
                    type="flat"
                    size="medium"
                    @click="validateSubmitComment"
                  >
                    {{ submitButtonText }}
                  </dd-button>
                </el-tooltip>
              </template>
              <template v-else>
                <dd-button
                  :is-loading="isSubmitting || isTrafficCopChecking"
                  color="blue"
                  class="marker--comment-reply--comment-btn"
                  type="flat"
                  size="medium"
                  @click="validateSubmitComment"
                >
                  {{ submitButtonText }}
                </dd-button>
              </template>
            </template>
          </div>
        </template>
      </div>
      <!-- End Body -->
    </el-form>
    <!-- Footer -->
    <div class="comment-or-reply__footer">
      <watchers-picker
        v-model:selected-ids="selectedWatcherIds"
        :is-disabled="$permissions.isGuest"
        :options="watchersOptions"
        :current-user-id="author.id"
        :type="type"
      />
    </div>
    <!-- End Footer -->
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import omit from 'lodash-es/omit'

/* Mixins */

/* Components */

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'
import LocalStore from '@/helpers/localstore'

/* Data types */
import { EEntities } from '@/types'
import { ICommentOrReplyData } from '@/types/data-objects/items'
import { IAttachment } from '@/types/data-objects/attachments'
import {
  IAtMentionUser,
  IConvertedSavedReply,
  IItemResponder,
  IPersonBase,
  IPersonWithEmail,
  IPriorityLevelForSelection,
  IStatusForSelection,
} from '@/api/types/response.types'

/* API */
import FilesAPI from '@/api/modules/files.api'
import { IJobInfo } from '@/types/data-objects/items'
import { IChronologyItemTypes } from '@/types/data-objects/item-histories'

export default defineComponent({
  name: 'CommentOrReply',

  props: {
    entityId: {
      type: Number,
      required: true,
    },

    itemId: {
      type: Number,
      required: true,
    },

    author: {
      type: Object as () => IPersonBase,
      default: null,
    },

    cc: {
      type: Array as () => string[],
      default: (): string[] => [],
    },

    customer: {
      type: Object as () => IPersonWithEmail,
      default: null,
    },

    /* Editor */
    mentionUsers: {
      type: Array as () => IAtMentionUser[],
      required: true,
    },

    /* Assignee picker */
    assigneeId: {
      type: Number,
      default: null,
    },

    assigneesOptions: {
      type: Array as () => IPersonBase[],
      default: (): IPersonBase[] => [],
    },

    /* Priority */
    priorityId: {
      type: Number,
      required: true,
    },

    priorityOptions: {
      type: Array as () => IPriorityLevelForSelection[],
      required: true,
    },

    /* Status */
    statusId: {
      type: Number,
      required: true,
    },

    statusOptions: {
      type: Array as () => IStatusForSelection[],
      required: true,
    },

    /* Due date */
    dueDate: {
      type: String,
      default: null,
    },

    /* Watchers picker */
    watcherIds: {
      type: Array as () => number[],
      required: true,
    },

    watchersOptions: {
      type: Array as () => IPersonWithEmail[],
      required: true,
    },

    replySignature: {
      type: String,
      default: '',
    },

    type: {
      type: String as () => EEntities,
      default: EEntities.MAILBOXES,
    },

    savedReplies: {
      type: Array as () => IConvertedSavedReply[],
      default: (): IConvertedSavedReply[] => [],
    },

    respondersSinceLastView: {
      type: Array as () => IItemResponder[],
      default: (): IItemResponder[] => [],
    },

    jobInfo: {
      type: Object as () => IJobInfo,
      default: null,
    },

    isTrafficCopChecking: {
      type: Boolean,
      default: false,
    },

    isSubmitting: {
      type: Boolean,
      default: false,
    },

    isPostSubmitPreRefetchData: {
      type: Boolean,
      default: false,
    },

    hasGuestUsers: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'confirmed-traffic-cop-note',
    'editor-opened',
    'submit',
    'update-cc',
    'update-watchers',
  ],

  data() {
    return {
      ColorAssistant,
      mode: 'comment',
      isEditing: false,
      formData: {
        mode: '',
        to: '',
        cc: [],
        message: '',
        assigneeID: null,
        dueDate: null,
        statusID: null,
        priorityID: null,
        listWatcherIDs: [],
        listAtMentionIDs: [],
        listFileUploadIDs: [],
      } as ICommentOrReplyData,
      internalAttachments: [] as any[],
      internalCustomer: {
        email: '',
        name: '',
        id: 0,
      },
      autoFocusEditor: false,
    }
  },

  computed: {
    isInternalCustomerEmpty(): boolean {
      return !this.internalCustomer || this.internalCustomer.email === ''
    },
    replyToLabel(): string {
      /* Case user has X'd out the customer and waiting to add a new one */
      /* Reply button will disable too. */
      if (this.isInternalCustomerEmpty) {
        return 'Reply'
      } else if (this.$screen.isTabletOrDesktop(this.$mq)) {
        return `Reply to ${this.internalCustomer.name}`
      } else {
        return `Reply to Customer`
      }
    },
    hasJobs(): boolean {
      return this.jobInfo !== null
    },
    hasRespondersSinceLastView(): boolean {
      return this.respondersSinceLastView.length > 0
    },
    trafficCopText(): string {
      if (!this.hasRespondersSinceLastView) {
        return ''
      }

      if (this.respondersSinceLastView.length > 1) {
        return `✋ <strong>Wait! Before we send this...</strong><br><strong>${
          this.respondersSinceLastView.length
        } responses</strong> were added since you started responding. <u>Click here to see what they wrote</u>.`
      }

      const lastResponder = this.respondersSinceLastView[0]

      if (lastResponder.isReply) {
        return `✋ <strong>Wait! Before we send this...</strong><br><strong>${
          lastResponder.name
        }</strong> replied since you started responding. <u>Click here to see what they wrote</u>.`
      } else {
        return `✋ <strong>Wait! Before we send this...</strong><br><strong>${
          lastResponder.name
        }</strong> commented since you started responding. <u>Click here to see what they wrote</u>.`
      }
    },
    possiblyMeantToComment(): boolean {
      return this.formData.message.includes('<span class="mention"')
    },

    possiblyMeantToCommentText(): string {
      if (!this.possiblyMeantToComment) {
        return ''
      }
      return `✋ You @-mentioned someone. <u>Click here to add a comment</u> or click below if you really mean to reply to the customer.`
    },
    selectedWatcherIds: {
      get(): number[] {
        return this.watcherIds
      },
      set(newValue: number[]) {
        this.$emit('update-watchers', newValue)
      },
    },
    avatarSize(): number {
      if (this.$screen.isDesktop(this.$mq)) {
        return 40
      }
      if (this.$screen.isTablet(this.$mq)) {
        return 24
      }
      // Mobile
      return 16
    },
    isCommentActive(): boolean {
      return this.mode === IChronologyItemTypes.COMMENT
    },
    isInternalCommentActive(): boolean {
      return this.mode === IChronologyItemTypes.INTERNAL_COMMENT
    },
    isAssignedToSelf(): boolean {
      return this.formData.assigneeID === this.$permissions.loggedInUser.id
    },
    isForConversation(): boolean {
      return this.type === EEntities.MAILBOXES
    },
    isReplyActive(): boolean {
      return this.mode === 'reply'
    },
    placeholder(): string {
      if (this.isReplyActive) {
        if (this.isInternalCustomerEmpty) {
          return ''
        } else if (this.internalCustomer.name !== this.internalCustomer.email) {
          return `Reply to ${this.internalCustomer.name} (${
            this.internalCustomer.email
          })...`
        } else {
          return `Reply to ${this.internalCustomer.email}`
        }
      }
      if (this.isForConversation) {
        return `Leave a comment. ${
          this.internalCustomer.name
        } won't see this...`
      }
      return `Leave a comment...`
    },

    editorClass(): string {
      if (this.expandEditor) {
        return 'comment-or-reply__body-message-editor--open'
      }

      return 'comment-or-reply__body-message-editor--default'
    },

    showStatusesSplayed(): boolean {
      return this.statusOptions.length > 1 && this.statusOptions.length <= 3
    },

    curStatus(): IStatusForSelection {
      /*
        Note: This can be "undefined" for a brief period of time on a meta-data update
        because the statusID prop from the task/convo hasn't updated yet, while the new status options have.
        This should be more rare now because the fetchTask/ConvoData() method is called first (previously it was
        last) in the Promisesof the task/convo detail fetchData() method. But, still it's a race condition,
        so it's not guaranteed. All told, this is why we do a v-if="curStatus" on the <status-selection-button> above. */
      return this.statusOptions.find(s => s.id === this.statusId)
    },

    splayedStatuses(): IStatusForSelection[] {
      if (!this.showStatusesSplayed) {
        return null
      }
      /* Grab the statuses after the first one (which is the current one) to display.*/
      return this.statusOptions.slice(1)
    },

    expandEditor(): boolean {
      return this.isEditing && !this.isPostSubmitPreRefetchData
    },

    moveStatusDirection(): string {
      return this.$screen.isMobilePortrait(this.$mq) ? 'down' : 'right'
    },

    submitButtonText(): string {
      const statusChanged: boolean = this.statusId !== this.formData.statusID
      const priorityChanged: boolean =
        this.priorityId !== this.formData.priorityID
      const assigneeChanged: boolean =
        this.assigneeId !== this.formData.assigneeID
      const dueDateChanged: boolean = this.dueDate !== this.formData.dueDate

      const anyChanged: boolean =
        statusChanged || priorityChanged || assigneeChanged || dueDateChanged

      let result
      if (this.isCommentActive) {
        result = 'Add Comment'
      } else if (this.isInternalCommentActive) {
        result = 'Add Internal Comment'
      } else {
        if (this.isInternalCustomerEmpty) {
          result = 'Send Reply'
        } else {
          result = `Send Reply to ${this.internalCustomer.name}`
        }
      }

      if (!anyChanged) {
        return result
      }

      const shorten =
        (this.isReplyActive && this.$screen.isTabletOrMobile(this.$mq)) ||
        this.$screen.isMobilePortrait(this.$mq)

      if (shorten) {
        return (result += ' & Update')
      }

      result += ' & Update '

      const changeArray = []

      if (statusChanged) {
        changeArray.push('Status')
      }
      if (priorityChanged) {
        changeArray.push('Priority')
      }
      if (assigneeChanged) {
        changeArray.push('Assignee')
      }
      if (dueDateChanged) {
        changeArray.push('Due Date')
      }

      for (let i = 0; i < changeArray.length; i++) {
        if (i > 0) {
          if (changeArray.length > 2 && i < changeArray.length - 1) {
            result += ', '
          } else {
            result += ' and '
          }
        }
        result += changeArray[i]
      }

      return result
    },
  },

  watch: {
    dueDate(newVal) {
      this.formData.dueDate = newVal
    },
    statusId(newVal) {
      this.formData.statusID = newVal
    },
    priorityId(newVal) {
      this.formData.priorityID = newVal
    },
    assigneeId(newVal) {
      this.formData.assigneeID = newVal
    },
    cc: {
      deep: 1,
      handler(newVal) {
        this.formData.cc = newVal
      },
    },
    /* Scenario -- updated from customer name area on conversation-detail-header, override any
       update made to the "to" field here. */
    customer(newVal) {
      this.internalCustomer = newVal
      this.formData.to = newVal.email
    },
    'formData.message': {
      handler(value) {
        this.saveTextToLocalStorage(value)
      },
    },
    isEditing: {
      immediate: true,
      handler(value) {
        this.$emit('editor-opened', value)
      },
    },
    isSubmitting: {
      immediate: true,
      handler(newVal: boolean, oldVal: boolean) {
        // A finished submission...
        if (oldVal && !newVal) {
          this.isEditing = false
        }
      },
    },
    isPostSubmitPreRefetchData: {
      immediate: true,
      handler(newVal: boolean, oldVal: boolean) {
        // Post submission but refetching detail data...
        if (!oldVal && newVal) {
          this.clearEditor()
        }
      },
    },
  },

  mounted() {
    this.initializeFormData()
    this.initializeEditorSetup()
  },

  methods: {
    saveTextToLocalStorage(value: string) {
      if (this.isTextEmpty(value)) {
        LocalStore.clearCommentOrReplyText(this.itemId)
        return
      }

      LocalStore.setCommentOrReplyText(this.itemId, value)
    },
    getSavedTextFromLocalStorage() {
      const saved = LocalStore.getCommentOrReplyText(this.itemId)

      if (this.isTextEmpty(saved)) {
        return ''
      }

      return saved
    },

    /* Text editor setup */
    initializeEditorSetup() {
      // If previous mode was a reply, re-enable the reply...
      if (LocalStore.isDefaultToReplyMode(this.itemId)) {
        this.updateMode('reply')
      }
      // See if we have saved text from local storage...
      this.formData.message = this.getSavedTextFromLocalStorage()
      // If we have data, expose editing mode...
      if (!this.isTextEmpty(this.formData.message)) {
        this.isEditing = true
      }
    },

    isTextEmpty(text: string) {
      return text === '<p><br></p>' || text === null || text === ''
    },

    enableComment() {
      this.updateMode(IChronologyItemTypes.COMMENT)
      LocalStore.clearDefaultToReplyMode(this.itemId)
      this.openEditor()
    },

    enableInternalComment() {
      this.updateMode(IChronologyItemTypes.INTERNAL_COMMENT)
      LocalStore.clearDefaultToReplyMode(this.itemId)
      this.openEditor()
    },

    enableReply() {
      this.updateMode('reply')
      LocalStore.setDefaultToReplyMode(this.itemId, true)
      this.openEditor()
    },

    updateMode(mode: string) {
      this.mode = mode
    },

    clearEditor() {
      // Clear local storage text settings...
      LocalStore.clearCommentOrReplyText(this.itemId)
      this.internalAttachments = []
      this.formData.message = ''
      this.formData.listAtMentionIDs = []
      this.formData.listFileUploadIDs = []
    },

    validateSubmitComment() {
      const noAttachments = this.formData.listFileUploadIDs.length === 0
      const textEmpty = this.isTextEmpty(this.formData.message)
      if (textEmpty && noAttachments) {
        this.$messaging.showError(`Please enter a comment.`)
        return
      }
      this.submitComment()
    },

    validateSubmitReply() {
      const noAttachments = this.formData.listFileUploadIDs.length === 0
      const textEmpty = this.isTextEmpty(this.formData.message)
      if (textEmpty && noAttachments) {
        this.$messaging.showError(`Please enter a reply.`)
        return
      }
      this.submitReply()
    },

    async submitComment() {
      this.formData.mode = this.mode
      const formData = omit(this.formData, ['to', 'cc'])
      this.$emit('submit', formData)
      await this.$nextTick()
    },

    async submitReply() {
      this.formData.mode = this.mode
      this.$emit('submit', this.formData)
      await this.$nextTick()
    },

    insertMentions(matches: any) {
      this.formData.listAtMentionIDs = matches
    },

    addFileHandler(files: IAttachment[]) {
      this.isEditing = true
      this.internalAttachments = files
      this.formData.listFileUploadIDs = files.map(item => item.id)
    },

    /**
     * Remove file from FormData
     */
    async removeFileHandler(id: number) {
      try {
        await FilesAPI.delete_Delete(id)
        this.internalAttachments = this.internalAttachments.filter(
          item => item.id !== id,
        )
        this.formData.listFileUploadIDs = this.internalAttachments.map(
          item => item.id,
        )
      } catch (error) {
        this.$messaging.showGenericError()
      }
    },

    initializeFormData() {
      this.formData.assigneeID = this.assigneeId
      this.formData.statusID = this.statusId
      this.formData.priorityID = this.priorityId
      this.formData.dueDate = this.dueDate
      this.formData.listWatcherIDs = this.watcherIds

      if (this.isForConversation) {
        this.formData.to = this.customer.email
        this.formData.cc = this.cc

        /* Decoupling ref from customer */
        this.internalCustomer = {
          email: this.customer.email,
          name: this.customer.name,
          id: this.customer.id,
        }
      }
    },

    updateToField(email: string) {
      this.formData.to = email
      this.internalCustomer = {
        email,
        name: email,
        id: 0,
      }
    },

    openEditor() {
      this.isEditing = true
      this.autoFocusEditor = true
    },
  },
})
</script>

<style lang="scss">
.comment-or-reply {
  position: relative;

  &__header {
    display: flex;
  }

  &__header-button {
    position: relative;
    padding: 0;
    margin-right: 24px;
    @include lettering--p-sm;
    font-weight: $bold;

    &--active.dd-button--text {
      color: var(--color-text-secondary);

      &::after {
        position: absolute;
        right: 0;
        bottom: -1px;
        left: 0;
        height: 2px;
        content: '';
        background: $color--blue;
        border-radius: 1px;
      }
    }
  }

  &__header-button-icon {
    margin-left: 5px;
    font-size: 11px;
  }

  &__body {
    margin-top: 24px;

    &.email-style {
      @include media-breakpoint-up(md) {
        padding: 30px;
        margin-left: -30px;
        margin-right: -30px;
        background-color: var(--color-background-header);
        border: solid 1px $color--gray-45;
        border-radius: 4px;
        @include standard-box-shadow;
      }
    }
  }

  &__body-message-wrapper {
    position: relative;

    .el-form-item {
      margin-bottom: 0;
    }
  }

  &__body-message-avatar {
    position: absolute !important;
    top: 0;

    @include media-breakpoint-up(md) {
      left: -0.5 * $detail-gutter-pad-md;
    }
    @include media-breakpoint-up(lg) {
      left: -0.5 * $detail-gutter-pad-lg;
    }
  }

  .el-form-item__content {
    .el-form-item__error {
      position: absolute;
      left: 4px;
      padding-top: 2px;
    }
  }

  /* Editor styles for "closed" state. Open state inherits from editor.vue.*/
  &__body-message-editor {
    &.editor {
      .editor__canvas {
        transition: min-height 250ms ease-in-out;
      }
    }

    &--default.editor {
      .ql-toolbar {
        display: none;
      }

      .editor__canvas {
        min-height: 94px;
        border-top: $border--solid-light;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
      }
    }
  }

  span.action-title {
    @include lettering--p-sm--no-color;
    color: var(--color-text-third);
    margin-bottom: 6px;
  }

  &__body-options {
    display: flex;
    @include lettering--p-sm;
    border: $border--solid-light;
    border-top: none;
    border-bottom: none;
    background-color: var(--color-background-header);

    @include media-breakpoint-down(md) {
      flex-direction: column;
    }

    > .status-section {
      display: flex;
      max-width: 63%;
      flex: 1;

      @include media-breakpoint-up(lg) {
        &.with-jobs {
          max-width: 70%;
        }
      }

      @include media-breakpoint-down(md) {
        max-width: 100%;
        border-bottom: $border--solid-light;
      }

      @include media-breakpoint-down(xs) {
        flex-direction: column;
      }

      > .change-status,
      > .current-status {
        display: flex;
        flex-direction: column;
        flex: 1;
        padding: 10px;

        /* When displayed as row, cap widths so statuses don't bleed... */
        @include media-breakpoint-up(sm) {
          > * {
            max-width: 240px;
          }
        }

        @include media-breakpoint-up(md) {
          > * {
            max-width: 256px;
          }
        }

        @include media-breakpoint-up(lg) {
          > * {
            max-width: 186px;
          }
        }
      }

      > .current-status {
        padding-right: 20px;
        background-color: var(--color-background-secondary);
        border-right: $border--dotted-light;

        @include media-breakpoint-down(xs) {
          padding-right: 10px;
          padding-bottom: 20px;
        }
      }

      > .change-status {
        position: relative;
        padding-left: 20px;
        border-right: $border--solid-light;

        @include media-breakpoint-down(xs) {
          padding-left: 10px;
          padding-top: 20px;

          /* Full-width status picker on mobile portrait */
          .status-picker {
            .select-button__button,
            .sbwam__button {
              width: 100%;

              &-content {
                justify-content: center;
              }
            }
          }
        }

        .status-options-splayed {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        @include media-breakpoint-down(md) {
          border-right: none;
        }

        .move-icon {
          position: absolute;
          left: -12px;
          top: 38px;

          @include media-breakpoint-down(xs) {
            top: -10px;
            left: calc(50% - 12px);
          }
        }
      }
    }

    > .assign-and-prioritize {
      display: flex;
      flex-direction: column;
      /* Just enough space for due-date-picker containing area */
      position: relative;
      padding: 10px;

      > .picker-section {
        /* Making this absolute because of due-date-picker width so it "floats" over its space */
        position: absolute;
        top: 36px;
        display: flex;
        min-width: 280px;

        @include media-breakpoint-down(md) {
          position: relative;
          top: 0;
          min-width: auto;
        }

        .assignee-picker {
          margin-top: -1px;
        }

        @include flex-row-horizontal-spacing(6px);
      }
    }
  }

  &__body-submit {
    display: flex;
    justify-content: flex-end;
    padding: 10px;
    border: $border--solid-light;
    background-color: var(--color-background-header);
    border-radius: 0 0 8px 8px;

    &.email-style {
      padding: 30px 0 0 0;
      background-color: transparent;
      border-left: none;
      border-right: none;
      border-bottom: none;
      justify-content: center;
    }
  }

  &__footer {
    border-top: $border--solid-light;
    margin-top: 24px;
    padding-top: 24px;
  }
}
</style>
